<script setup>
import RevenueLoadedItemsTable from './RevenueLoadedItemsTable.vue'
import BaseMetricStrip from '@/components/base/BaseMetricStrip.vue'
import BaseInfoLine from '../base/BaseInfoLine.vue'
import RevenueDocketActionsMenu from './RevenueDocketActionsMenu.vue'

import { computed, defineEmits, defineProps } from 'vue'
import { calculateInvoiceTotal } from '@/utils/revenueHelpers.js'
import {
  formatCurrency,
  formatDecimals,
  formatDate
} from '@/helpers/stringHelpers'
import {
  INVOICE_STATUS,
  getInvoiceStatusColor,
  getInvoiceStatusLabel,
  isInvoicedStatus
} from '@/constants/invoiceStatus'

const props = defineProps({
  item: {
    type: Object,
    required: true
  },
  loading: {
    type: Boolean,
    default: false
  },
  isCartonItem: {
    type: Boolean,
    default: false
  },
  cartonHeaders: {
    type: Array,
    default: () => []
  },
  porkerHeaders: {
    type: Array,
    default: () => []
  },
  freightCode: {
    type: Function,
    required: true
  }
})

const emit = defineEmits([
  'update-freight',
  'update-invoice',
  'update-invoice-status',
  'update-customer'
])

const invoiceStatus = computed(() => {
  return props.item.invoice_status || INVOICE_STATUS.DRAFT
})

const isLocked = computed(() => {
  return isInvoicedStatus(invoiceStatus.value)
})

const loadedItemHeaders = computed(() => {
  return props.isCartonItem ? props.cartonHeaders : props.porkerHeaders
})

const metrics = computed(() => {
  const { loaded_date, total_weight, total_cartons, total_pigs } =
    props.item?.job_description || {}
  return [
    { label: 'Date Loaded', value: loaded_date },
    { label: 'Total Weight', value: `${formatDecimals(total_weight)}kg` },
    { label: 'Total Cartons', value: total_cartons, show: props.isCartonItem },
    { label: 'Order Totals', value: total_pigs, show: !props.isCartonItem }
  ]
})

const invoiceTotal = computed(() => {
  return calculateInvoiceTotal(props.item?.loaded_items)
})

const invoiceTooltipTitle = computed(() => {
  return isLocked.value ? 'Invoice Locked' : 'Update Invoice Number'
})

const invoiceTooltipText = computed(() => {
  return isLocked.value ? 'This docket has been invoiced.' : 'Click to edit.'
})

const freightTooltipTitle = computed(() => {
  return isLocked.value ? 'Delivery Company Locked' : 'Update Delivery Company'
})

const freightTooltipText = computed(() => {
  return isLocked.value
    ? 'This docket has been invoiced.'
    : 'Click on names to edit.'
})

const updateInvoice = () => {
  if (isLocked.value) return

  emit('update-invoice', props.item)
}

const updateFreight = () => {
  if (isLocked.value) return

  emit('update-freight', props.item)
}

const updateStatus = () => {
  emit('update-invoice-status')
}
const updateCustomer = () => {
  emit('update-customer')
}
</script>

<template>
  <v-card class="mb-3" :loading="loading">
    <v-card-text>
      <v-row no-gutters class="mb-4 align-center">
        <div class="font-weight-bold grey--text text--darken-1 font-italic">
          {{ formatDate(item.date) }}
        </div>

        <v-spacer />

        <div class="d-flex align-baseline text-left">
          <BaseInfoLine
            label="Invoice"
            :value="item.invoice"
            editable
            :tooltip-title="invoiceTooltipTitle"
            :tooltip-text="invoiceTooltipText"
            :tooltip-icon="isLocked ? ['fas', 'lock'] : ['fas', 'edit']"
            @click="updateInvoice"
          />

          <div class="text-no-wrap mx-3">
            <span class="font-weight-bold grey--text text--darken-1 mr-1">
              Docket:
            </span>
            <span class="font-italic">{{ item.docket }}</span>
          </div>
        </div>

        <RevenueDocketActionsMenu
          :item="item"
          :invoice-status="invoiceStatus"
          :loading="loading"
          @update-invoice-status="updateStatus"
          @update-customer="updateCustomer"
        />
      </v-row>

      <v-row no-gutters class="mb-4">
        <h4 class="mb-0">{{ item.destination.name }}</h4>
        <v-spacer />
        <v-chip small :color="getInvoiceStatusColor(invoiceStatus)" dark>
          {{ getInvoiceStatusLabel(invoiceStatus) }}
        </v-chip>
      </v-row>

      <div class="mb-2 d-flex justify-start">
        <BaseInfoLine
          label="Delivered By"
          :value="freightCode(item.delivery_co_ids)"
          icon="fas fa-truck-arrow-right"
          icon-class="text-info"
          editable
          :tooltip-title="freightTooltipTitle"
          :tooltip-text="freightTooltipText"
          :tooltip-icon="isLocked ? ['fas', 'lock'] : ['fas', 'edit']"
          @click="updateFreight"
        />
      </div>

      <div class="mb-2 d-flex justify-start">
        <BaseInfoLine
          label="Depot"
          :value="item.depot.name"
          icon="fas fa-map-marker-alt"
          icon-class="text-danger"
        />
      </div>

      <BaseMetricStrip
        :metrics="metrics"
        :total-price="`${formatCurrency(invoiceTotal)}`"
        total-label="Invoice Total"
      />

      <div class="mt-2 text-left">
        <RevenueLoadedItemsTable
          :items="item.loaded_items"
          :headers="loadedItemHeaders"
          :loading="loading"
        />
      </div>
    </v-card-text>
  </v-card>
</template>

<style scoped>
.editable-display:hover {
  font-weight: 700;
  text-decoration: underline;
}

p {
  margin-bottom: 0;
  line-height: 1.1;
}

.multiple-freight-label {
  font-size: 14px;
  line-height: 1.1;
}
</style>
